/**
 * HAWK Authentication Implementation
 *
 * Generates HAWK authorization headers using HMAC-based signature.
 * Reference: https://github.com/mozilla/hawk
 */

export interface HawkConfig {
  authId: string;
  authKey: string;
  algorithm: 'sha256' | 'sha1';
  ext?: string;
  includePayloadHash: boolean;
}

interface HawkHeaderOptions {
  method: string;
  url: string;
  body?: string;
  contentType?: string;
}

/**
 * Generate a random nonce string
 */
function generateNonce(length: number = 8): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += chars[randomValues[i] % chars.length];
  }
  return result;
}

/**
 * Compute HMAC signature using Web Crypto API
 */
async function computeHmac(
  algorithm: 'sha256' | 'sha1',
  key: string,
  data: string
): Promise<string> {
  const hashAlgorithm = algorithm === 'sha256' ? 'SHA-256' : 'SHA-1';

  // Import key
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: hashAlgorithm },
    false,
    ['sign']
  );

  // Sign data
  const signature = await crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    encoder.encode(data)
  );

  // Convert to base64
  return btoa(String.fromCharCode(...new Uint8Array(signature)));
}

/**
 * Compute hash for payload
 */
async function computePayloadHash(
  algorithm: 'sha256' | 'sha1',
  contentType: string,
  payload: string
): Promise<string> {
  const hashAlgorithm = algorithm === 'sha256' ? 'SHA-256' : 'SHA-1';

  // Normalize content type
  const normalizedContentType = contentType?.split(';')[0]?.trim()?.toLowerCase() || '';

  // Build string to hash
  const data = `hawk.1.payload\n${normalizedContentType}\n${payload}\n`;

  // Hash
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest(hashAlgorithm, encoder.encode(data));

  return btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
}

/**
 * Parse URL into components needed for HAWK signature
 */
function parseUrl(urlString: string): { host: string; port: number; path: string } {
  const url = new URL(urlString);

  const defaultPorts: Record<string, number> = {
    'http:': 80,
    'https:': 443,
  };

  const port = url.port ? parseInt(url.port, 10) : defaultPorts[url.protocol] || 443;
  const path = url.pathname + url.search;

  return {
    host: url.hostname.toLowerCase(),
    port,
    path,
  };
}

/**
 * Generate HAWK Authorization header
 */
export async function generateHawkHeader(
  config: HawkConfig,
  options: HawkHeaderOptions
): Promise<string> {
  const { method, url, body, contentType } = options;
  const { authId, authKey, algorithm, ext, includePayloadHash } = config;

  const timestamp = Math.floor(Date.now() / 1000);
  const nonce = generateNonce();
  const { host, port, path } = parseUrl(url);

  // Compute payload hash if needed
  let hash: string | undefined;
  if (includePayloadHash && body) {
    hash = await computePayloadHash(algorithm, contentType || '', body);
  }

  // Build normalized string
  const normalizedParts = [
    'hawk.1.header',
    timestamp.toString(),
    nonce,
    method.toUpperCase(),
    path,
    host,
    port.toString(),
    hash || '',
    ext || '',
    '',
  ];
  const normalized = normalizedParts.join('\n');

  // Compute MAC
  const mac = await computeHmac(algorithm, authKey, normalized);

  // Build header
  const headerParts: string[] = [
    `id="${authId}"`,
    `ts="${timestamp}"`,
    `nonce="${nonce}"`,
    `mac="${mac}"`,
  ];

  if (hash) {
    headerParts.push(`hash="${hash}"`);
  }

  if (ext) {
    // Escape ext value
    const escapedExt = ext.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    headerParts.push(`ext="${escapedExt}"`);
  }

  return `Hawk ${headerParts.join(', ')}`;
}

export default generateHawkHeader;
